import React, { useEffect, useState } from 'react';
import { Check, Edit3, Plus } from 'react-feather';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import SchoolSelector from '../SchoolSelector';
import { isVerifiedSchoolExam } from '../../../utils/examUtils';
import { hapticsImpactLight } from '../../../utils/haptics';
import CustomModal from '../CustomModal';
import { Capacitor } from '@capacitor/core';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { TriangleDown } from '../../../assets/svgs/TriangleDown';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';

interface ExamSelectProps {
  onClick: (examId: string) => void;
  addExam: () => void;
  updatePrimaryExam: (examId: string, setPrimary?: boolean) => Promise<void>;
}

const ExamSelect: React.FC<ExamSelectProps> = ({
  onClick,
  addExam,
  updatePrimaryExam,
}) => {
  const stepExams = useSelector(examsSelectors.selectStepExams);
  const shelfExams = useSelector(examsSelectors.selectShelfExams);
  const schoolExams = useSelector(examsSelectors.selectSchoolExams);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const school = useSelector((state: RootState) => state.school.school);

  const [showSchoolSelector, setShowSchoolSelector] = useState(false);
  const [showSchoolSelectorModal, setShowSchoolSelectorModal] = useState(false);
  const [schoolExamsSetIsOpen, setSchoolExamsSetIsOpen] = useState(false);

  const windowWidth = useWindowWidth();

  useEffect(() => {
    if (school?.id && primaryExam) {
      if (
        isVerifiedSchoolExam(primaryExam) &&
        primaryExam.school?.id !== school.id
      ) {
        updatePrimaryExam(primaryExam.id, false);
      }
    }
  }, [school?.id]);

  return (
    <>
      {Capacitor.isNativePlatform() || windowWidth <= 560 ? (
        <div className="mobile-exam-select">
          {(stepExams || []).map((exam) => (
            <button
              key={'exam-' + exam.id}
              className={`mobile-exam-select_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
              onClick={() => onClick(exam.id)}
              tabIndex={0}
              aria-label={`Select ${exam.name}`}
            >
              {exam.name}
              {exam.id === primaryExam?.id && <Check />}
            </button>
          ))}
          <Accordion>
            <AccordionSummary
              aria-controls="exam-content"
              id="exam-header"
              sx={{ padding: '0', minHeight: '0 !important' }}
              onClick={() => {
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Shelf Exams"
            >
              <label className="mobile-exam-select_exam mobile-exam-select_exam--label">
                <TriangleDown classes="expand-icon expand-icon--open" />
                <TriangleRight classes="expand-icon expand-icon--closed" />
                <span>Shelf Exams</span>
              </label>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 0 1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                {(shelfExams || []).map((exam) => (
                  <button
                    key={'exam-' + exam.id}
                    className={`mobile-exam-select_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                    onClick={() => onClick(exam.id)}
                    tabIndex={0}
                    aria-label={`Select ${exam.name.replace('Shelf Exam', '')}`}
                  >
                    {exam.name.replace('Shelf Exam', '')}
                    {exam.id === primaryExam?.id && <Check />}
                  </button>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion
            classes={{ root: 'school-accordion' }}
            expanded={schoolExamsSetIsOpen}
          >
            <AccordionSummary
              aria-controls="exam-content"
              id="exam-header-school"
              sx={{ padding: '0', minHeight: '0 !important' }}
              onClick={() => {
                hapticsImpactLight();
                if (!school) {
                  setShowSchoolSelectorModal(true);
                }
                setSchoolExamsSetIsOpen(!schoolExamsSetIsOpen);
              }}
              tabIndex={0}
              aria-label="Precliical Exams"
            >
              <label
                className={`mobile-exam-select_exam mobile-exam-select_exam--label mobile-exam-select_exam--school-label ${school ? 'has-school' : ''}`}
                style={{ flex: 1 }}
              >
                <TriangleDown classes="expand-icon expand-icon--open" />
                <TriangleRight classes="expand-icon expand-icon--closed" />
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {school
                    ? school?.shortName
                      ? school?.shortName
                      : school?.name
                    : 'Preclinical'}{' '}
                  Exams
                </span>
              </label>
              {school && (
                <button
                  className="button button--change-school"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    hapticsImpactLight();
                    setShowSchoolSelectorModal(true);
                  }}
                  tabIndex={0}
                  aria-label="Change School"
                >
                  <Edit3 />
                </button>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 0 1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                {school && (
                  <>
                    {(schoolExams || []).length > 0 ? (
                      (schoolExams || []).map((exam) => (
                        <button
                          key={'exam-' + exam.id}
                          className={`mobile-exam-select_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                          onClick={() => onClick(exam.id)}
                          tabIndex={0}
                          aria-label={`Select ${exam.name.replace('Shelf Exam', '')}`}
                        >
                          <span className="custom-exam-name">
                            {exam.name.includes(' | ')
                              ? exam.name.split(' | ')[1]
                              : exam.name}
                          </span>
                          {exam.id === primaryExam?.id && <Check />}
                        </button>
                      ))
                    ) : (
                      <p style={{ margin: '.5rem 0' }}>
                        No official school exams yet.
                      </p>
                    )}
                  </>
                )}
                <button
                  className="button button--link button--add-exam"
                  onClick={() => {
                    addExam();
                    hapticsImpactLight();
                  }}
                  tabIndex={0}
                  aria-label="Create Custom Exam"
                >
                  <Plus />
                  Create Your Own Exam
                </button>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      ) : (
        <div className="available-exams">
          <div className="available-exams_top">
            <div className="available-exams_top_set available-exams_top_set--step">
              <div className="available-exams_top_set_title">Step</div>
              <div className="available-exams_top_set_exams">
                {(stepExams || []).map((exam) => (
                  <button
                    key={'exam-' + exam.id}
                    className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                    onClick={() => onClick(exam.id)}
                    tabIndex={0}
                    aria-label={`Select ${exam.name}`}
                  >
                    <TriangleRight />
                    {exam.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="available-exams_top_set available-exams_top_set--shelf">
              <div className="available-exams_top_set_title">Shelf</div>
              <div className="available-exams_top_set_exams">
                {(shelfExams || []).map((exam) => (
                  <button
                    key={'exam-' + exam.id}
                    className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                    onClick={() => onClick(exam.id)}
                    tabIndex={0}
                    aria-label={`Select ${exam.name.replace('Shelf Exam', '')}`}
                  >
                    <TriangleRight />
                    {exam.name.replace('Shelf Exam', '')}
                  </button>
                ))}
              </div>
            </div>
            <div
              className="available-exams_top_set available-exams_top_set--school"
              style={
                windowWidth > 1150 && showSchoolSelector
                  ? {
                      transform: 'translateY(-1.25rem)',
                    }
                  : {}
              }
            >
              {school ? (
                <>
                  {!showSchoolSelector && (
                    <div className="available-exams_top_set_title">
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '16rem',
                        }}
                      >
                        {school?.shortName ? school?.shortName : school?.name}
                      </div>
                      <button
                        className="button button--icon-only"
                        onClick={() => {
                          hapticsImpactLight();
                          setShowSchoolSelector(true);
                        }}
                        tabIndex={0}
                        aria-label="Change School"
                      >
                        <Edit3 />
                      </button>
                    </div>
                  )}
                  {showSchoolSelector && (
                    <div className="available-exams_top_set_title">
                      <SchoolSelector
                        onSelect={() => {
                          setShowSchoolSelector(false);
                          setSchoolExamsSetIsOpen(true);
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="available-exams_top_set_title">Preclinical</div>
              )}
              {!school && <SchoolSelector />}
              {school && (
                <div
                  className="available-exams_top_set_exams"
                  style={
                    (schoolExams || []).length > 0
                      ? { transform: 'translateY(-2px)' }
                      : {}
                  }
                >
                  {(schoolExams || []).length > 0 ? (
                    (schoolExams || []).map((exam) => (
                      <button
                        key={'exam-' + exam.id}
                        className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                        onClick={() => onClick(exam.id)}
                        tabIndex={0}
                        aria-label={`Select ${exam.name}`}
                      >
                        <TriangleRight />
                        <span className="custom-exam-name">
                          {exam.name.includes(' | ')
                            ? exam.name.split(' | ')[1]
                            : exam.name}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p style={{ margin: '.5rem 0' }}>
                      No official school exams yet.
                    </p>
                  )}
                  <button
                    className="button button--link button--add-exam"
                    onClick={() => {
                      addExam();
                      hapticsImpactLight();
                    }}
                    tabIndex={0}
                    aria-label="Create Custom Exam"
                  >
                    <Plus />
                    Create Your Own Exam
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <CustomModal
        name="school-select"
        open={showSchoolSelectorModal}
        onClose={() => setShowSchoolSelectorModal(false)}
        glass={true}
      >
        <div className="modal_header">
          <h3>Select School</h3>
        </div>
        <div className="modal_content">
          <SchoolSelector />
        </div>
        <div className="modal_actions">
          <button
            className="button"
            onClick={() => {
              setShowSchoolSelectorModal(false);
              setSchoolExamsSetIsOpen(true);
              hapticsImpactLight();
            }}
            tabIndex={0}
            aria-label="Confirm"
          >
            Confirm
          </button>
        </div>
      </CustomModal>
    </>
  );
};

export default ExamSelect;
